import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import UploadProgress from './UploadProgress';
import { uploadService } from '../services/uploadService';
import './ImageUpload.css';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_TYPES = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
  'image/gif': ['.gif']
};

const ImageUpload = () => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    // Handle rejected files
    if (rejectedFiles.length > 0) {
      const errors = rejectedFiles.map(file => {
        const error = file.errors[0];
        return `${file.file.name}: ${error.message}`;
      });
      alert(`Some files were rejected:\n${errors.join('\n')}`);
    }

    // Process accepted files
    const newFiles = acceptedFiles.map(file => ({
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      size: file.size,
      preview: URL.createObjectURL(file),
      status: 'pending', // pending, uploading, success, error
      progress: 0,
      error: null
    }));

    setFiles(prev => [...prev, ...newFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_TYPES,
    maxSize: MAX_FILE_SIZE,
    multiple: true
  });

  const removeFile = (fileId) => {
    setFiles(prev => {
      const file = prev.find(f => f.id === fileId);
      if (file && file.preview) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter(f => f.id !== fileId);
    });
  };

  const uploadFiles = async () => {
    if (files.length === 0) return;

    setUploading(true);

    for (const fileData of files) {
      if (fileData.status !== 'pending') continue;

      try {
        // Update status to uploading
        setFiles(prev => prev.map(f => 
          f.id === fileData.id 
            ? { ...f, status: 'uploading', progress: 0 }
            : f
        ));

        // Upload with progress tracking
        await uploadService.uploadFile(fileData.file, (progress) => {
          setFiles(prev => prev.map(f => 
            f.id === fileData.id 
              ? { ...f, progress }
              : f
          ));
        });

        // Mark as success
        setFiles(prev => prev.map(f => 
          f.id === fileData.id 
            ? { ...f, status: 'success', progress: 100 }
            : f
        ));

      } catch (error) {
        console.error('Upload failed:', error);
        setFiles(prev => prev.map(f => 
          f.id === fileData.id 
            ? { ...f, status: 'error', error: error.message }
            : f
        ));
      }
    }

    setUploading(false);
  };

  const clearCompleted = () => {
    setFiles(prev => {
      const completed = prev.filter(f => f.status === 'success' || f.status === 'error');
      completed.forEach(file => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });
      return prev.filter(f => f.status === 'pending' || f.status === 'uploading');
    });
  };

  const pendingFiles = files.filter(f => f.status === 'pending');
  const completedFiles = files.filter(f => f.status === 'success' || f.status === 'error');

  return (
    <div className="image-upload">
      <div className="upload-header">
        <h2>Upload Images</h2>
        <p>Drag and drop images or click to select files. Maximum 10MB per file.</p>
      </div>

      <div 
        {...getRootProps()} 
        className={`dropzone ${isDragActive ? 'drag-active' : ''} ${files.length > 0 ? 'has-files' : ''}`}
      >
        <input {...getInputProps()} />
        <div className="dropzone-content">
          <div className="upload-icon">📁</div>
          <div className="dropzone-text">
            {isDragActive ? (
              <p>Drop the images here...</p>
            ) : (
              <>
                <p><strong>Click to select images</strong> or drag and drop</p>
                <p className="file-types">Supported: JPEG, PNG, WebP, GIF (max 10MB each)</p>
              </>
            )}
          </div>
        </div>
      </div>

      {files.length > 0 && (
        <div className="upload-actions">
          <button 
            onClick={uploadFiles}
            disabled={uploading || pendingFiles.length === 0}
            className="upload-button"
          >
            {uploading ? 'Uploading...' : `Upload ${pendingFiles.length} file${pendingFiles.length !== 1 ? 's' : ''}`}
          </button>
          
          {completedFiles.length > 0 && (
            <button 
              onClick={clearCompleted}
              className="clear-button"
              disabled={uploading}
            >
              Clear Completed
            </button>
          )}
        </div>
      )}

      {files.length > 0 && (
        <div className="files-list">
          {files.map(fileData => (
            <UploadProgress 
              key={fileData.id}
              fileData={fileData}
              onRemove={() => removeFile(fileData.id)}
              disabled={uploading}
            />
          ))}
        </div>
      )}

      {files.filter(f => f.status === 'success').length > 0 && (
        <div className="upload-success">
          <h3>✅ Upload Complete!</h3>
          <p>
            Your images are being processed and will be available on the CDN shortly. 
            You'll receive an email notification when processing is complete.
          </p>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
